import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { IoCheckmark } from 'react-icons/io5';
import { useVerifyPaymentMutation, useGetOrderDetailsQuery } from '../../slices/ordersApiSlice';
import { trackPurchase } from '../../utils/analytics';
import logo from '../../assets/logo.webp';

const WHATSAPP_NUMBER = '966548036906';
const GREEN = '#7C9D64';

// Pull GA4 client_id + session_id from the GA cookies so the server-side
// purchase event joins the visitor's session and keeps source attribution.
function readGaIds() {
  try {
    const cid = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    const sid = document.cookie.match(/_ga_HKDB9S1X9Q=GS\d+\.\d+\.s?(\d+)/);
    return { clientId: cid ? cid[1] : null, sessionId: sid ? sid[1] : null };
  } catch (e) {
    return { clientId: null, sessionId: null };
  }
}

function SuccessScreen() {
  const { i18n } = useTranslation();
  const isAr = i18n.language !== 'en';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderApi = queryParams.get('orderApi');
  const paymentId = queryParams.get('id'); // appended by Moyasar on redirect

  const [verifyPayment] = useVerifyPaymentMutation();
  // pending | paid | failed
  const [verifyState, setVerifyState] = useState('pending');
  const [failMessage, setFailMessage] = useState('');
  const { data: orderData } = useGetOrderDetailsQuery(orderApi, { skip: !orderApi });

  useEffect(() => {
    if (!orderApi) {
      setVerifyState('failed');
      return;
    }
    if (!paymentId) {
      setVerifyState('failed');
      setFailMessage(isAr ? 'لم يتم العثور على مرجع عملية الدفع.' : 'Missing payment reference.');
      return;
    }
    const ga = readGaIds();
    verifyPayment({ order: orderApi, payment_id: paymentId, ga_client_id: ga.clientId, ga_session_id: ga.sessionId })
      .unwrap()
      .then(() => setVerifyState('paid'))
      .catch((err) => {
        setVerifyState('failed');
        setFailMessage(err?.data?.message || '');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderApi, paymentId]);

  const order = orderData?.order || orderData || {};
  const lineItems = orderData?.orderItems || order.orderItems || order.items || [];

  useEffect(() => {
    if (verifyState === 'paid' && orderApi) {
      const items = lineItems.map((li) => {
        const p = li.product || li;
        return {
          id: p.id || li.product_id,
          slug: p.slug,
          name_en: p.name_en || p.name,
          name_ar: p.name_ar || p.name,
          price: li.price,
          qty: li.qty,
        };
      });
      trackPurchase(
        orderApi,
        items,
        order.totalPrice || order.total_price,
        order.shippingPrice || order.shipping_price || 0,
        i18n.language
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyState, orderData, orderApi, i18n.language]);

  const cur = isAr ? 'ر.س' : 'SAR';
  const money = (v) => `${Number(v || 0).toFixed(2)} ${cur}`;

  // ---------- pending ----------
  if (verifyState === 'pending') {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Spinner animation="border" style={{ color: GREEN }} />
        <p style={{ marginTop: 20, fontSize: 17, color: '#444' }}>
          {isAr ? 'نتحقق من عملية الدفع...' : 'Verifying your payment...'}
        </p>
      </div>
    );
  }

  // ---------- failed ----------
  if (verifyState === 'failed') {
    return (
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '48px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fdecea', color: '#d93025', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 18px' }}>
          ✕
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
          {isAr ? 'لم تكتمل عملية الدفع' : 'Your payment was not completed'}
        </h1>
        {failMessage && <p style={{ color: '#d93025', fontSize: 15 }}>{failMessage}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
          {orderApi && (
            <Link to={`/pay/${orderApi}`} style={{ textDecoration: 'none' }}>
              <button style={{ width: '100%', background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '13px', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
                {isAr ? 'إعادة محاولة الدفع' : 'Try payment again'}
              </button>
            </Link>
          )}
          <Link to={'/contact-us'} style={{ textDecoration: 'none' }}>
            <button style={{ width: '100%', background: '#fff', color: '#333', border: '1px solid #ccc', borderRadius: 10, padding: '13px', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
              {isAr ? 'تواصل معنا' : 'Contact us'}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // ---------- success (Shopify-style confirmation) ----------
  const firstName = (order.name || '').trim().split(' ')[0];
  const orderNo = order.id || orderApi;
  const contactLine = order.email || (order.phone ? `+${String(order.phone).replace(/^\+/, '')}` : '');

  const cardStyle = {
    background: '#fff',
    border: '1px solid #e6e8eb',
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
  };
  const sectionTitle = { fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 14 };
  const rowBetween = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 };
  const infoLabel = { fontSize: 12, color: '#8a8f98', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 };
  const infoValue = { fontSize: 14.5, color: '#2b2b2b', lineHeight: 1.5 };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 16px 56px' }}>
      {/* Confirmation header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: GREEN,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
            boxShadow: '0 6px 18px rgba(124,157,100,0.35)',
          }}
        >
          <IoCheckmark color="#fff" size={40} />
        </div>
        <div style={{ fontSize: 13, color: GREEN, fontWeight: 700, letterSpacing: 0.4, marginBottom: 6 }}>
          {isAr ? `طلب رقم #${orderNo}` : `Order #${orderNo}`}
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1a1a1a', margin: '0 0 8px' }}>
          {firstName
            ? isAr
              ? `شكراً لك، ${firstName}!`
              : `Thank you, ${firstName}!`
            : isAr
            ? 'شكراً لك!'
            : 'Thank you!'}
        </h1>
        <p style={{ fontSize: 16, color: '#3d3d3d', margin: '0 0 4px', fontWeight: 600 }}>
          {isAr ? 'تم تأكيد طلبك وجاري العمل على شحنه.' : 'Your order is confirmed and we are working on shipping it.'}
        </p>
        {contactLine && (
          <p style={{ fontSize: 14, color: '#8a8f98', margin: 0 }}>
            {isAr ? 'أرسلنا تفاصيل التأكيد إلى ' : "We've sent the confirmation to "}
            <span style={{ color: '#3d3d3d', fontWeight: 600, direction: 'ltr', display: 'inline-block' }}>{contactLine}</span>
          </p>
        )}
      </div>

      {/* Order summary */}
      <div style={cardStyle}>
        <div style={sectionTitle}>{isAr ? 'ملخص الطلب' : 'Order summary'}</div>

        {lineItems.map((li, idx) => {
          const p = li.product || li;
          const name = isAr ? p.name_ar || p.name_en : p.name_en || p.name_ar;
          const img = p.image_path || li.image_path;
          return (
            <div key={idx} style={{ ...rowBetween, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={img}
                    alt=""
                    style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid #eceef0', background: '#f6f6f6' }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: -8,
                      insetInlineEnd: -8,
                      minWidth: 22,
                      height: 22,
                      padding: '0 6px',
                      borderRadius: 11,
                      background: '#5b6b7b',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {li.qty}
                  </span>
                </div>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: '#2b2b2b', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </div>
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#2b2b2b', whiteSpace: 'nowrap' }}>
                {money(Number(li.price) * Number(li.qty))}
              </div>
            </div>
          );
        })}

        <div style={{ borderTop: '1px solid #eceef0', margin: '16px 0 14px' }} />

        <div style={{ ...rowBetween, marginBottom: 9 }}>
          <span style={{ fontSize: 14, color: '#6b7280' }}>{isAr ? 'المجموع الفرعي' : 'Subtotal'}</span>
          <span style={{ fontSize: 14, color: '#2b2b2b' }}>{money(order.itemsPrice)}</span>
        </div>
        <div style={{ ...rowBetween, marginBottom: 9 }}>
          <span style={{ fontSize: 14, color: '#6b7280' }}>{isAr ? 'الشحن' : 'Shipping'}</span>
          <span style={{ fontSize: 14, color: Number(order.shippingPrice) === 0 ? GREEN : '#2b2b2b', fontWeight: Number(order.shippingPrice) === 0 ? 700 : 400 }}>
            {Number(order.shippingPrice) === 0 ? (isAr ? 'مجاني' : 'Free') : money(order.shippingPrice)}
          </span>
        </div>

        <div style={{ borderTop: '1px solid #eceef0', margin: '14px 0 12px' }} />

        <div style={rowBetween}>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#1a1a1a' }}>{isAr ? 'الإجمالي' : 'Total'}</span>
          <span style={{ fontSize: 19, fontWeight: 800, color: '#1a1a1a' }}>{money(order.totalPrice)}</span>
        </div>
        {Number(order.taxPrice) > 0 && (
          <div style={{ textAlign: isAr ? 'left' : 'right', marginTop: 6 }}>
            <span style={{ fontSize: 12.5, color: '#8a8f98' }}>
              {isAr ? `شامل ضريبة القيمة المضافة ${Number(order.taxPrice).toFixed(2)} ر.س` : `Includes ${Number(order.taxPrice).toFixed(2)} SAR VAT`}
            </span>
          </div>
        )}
      </div>

      {/* Customer / shipping info */}
      <div style={cardStyle}>
        <div style={sectionTitle}>{isAr ? 'تفاصيل الشحن والتواصل' : 'Shipping & contact details'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {(order.name || contactLine) && (
            <div>
              <div style={infoLabel}>{isAr ? 'معلومات التواصل' : 'Contact'}</div>
              <div style={infoValue}>
                {order.name && <div>{order.name}</div>}
                {order.phone && <div style={{ direction: 'ltr', display: 'inline-block' }}>+{String(order.phone).replace(/^\+/, '')}</div>}
                {order.email && <div style={{ direction: 'ltr', display: 'inline-block' }}>{order.email}</div>}
              </div>
            </div>
          )}
          {(order.address || order.city || order.country) && (
            <div>
              <div style={infoLabel}>{isAr ? 'عنوان الشحن' : 'Shipping address'}</div>
              <div style={infoValue}>
                {order.address && <div>{order.address}</div>}
                <div>{[order.city, order.country].filter(Boolean).join('، ')}</div>
              </div>
            </div>
          )}
          <div>
            <div style={infoLabel}>{isAr ? 'طريقة الدفع' : 'Payment'}</div>
            <div style={{ ...infoValue, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: GREEN, fontWeight: 700 }}>✓</span>
              {isAr ? 'تم الدفع إلكترونياً' : 'Paid online'}
            </div>
          </div>
          <div>
            <div style={infoLabel}>{isAr ? 'طريقة الشحن' : 'Delivery'}</div>
            <div style={infoValue}>{isAr ? 'توصيل خلال 2-5 أيام عمل' : '2–5 business days'}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
        <Link to={'/'} style={{ textDecoration: 'none' }}>
          <button style={{ width: '100%', background: GREEN, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
            {isAr ? 'متابعة التسوق' : 'Continue shopping'}
          </button>
        </Link>
        <Link to={`/orders/${orderApi}`} style={{ textDecoration: 'none' }}>
          <button style={{ width: '100%', background: '#fff', color: '#2b2b2b', border: '1px solid #d4d7db', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>
            {isAr ? 'عرض تفاصيل الطلب' : 'View order details'}
          </button>
        </Link>
      </div>

      {/* Help line */}
      <div style={{ textAlign: 'center', marginTop: 22 }}>
        <span style={{ fontSize: 13.5, color: '#8a8f98' }}>
          {isAr ? 'تحتاج مساعدة؟ ' : 'Need help? '}
        </span>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 13.5, color: GREEN, fontWeight: 700, textDecoration: 'none' }}
        >
          {isAr ? 'تواصل معنا عبر واتساب' : 'Chat with us on WhatsApp'}
        </a>
      </div>

      {/* subtle brand mark */}
      <div style={{ textAlign: 'center', marginTop: 26, opacity: 0.5 }}>
        <img src={logo} alt="" style={{ height: 34 }} />
      </div>
    </div>
  );
}

export default SuccessScreen;
